import {useMemo} from 'react';
import {
  IN_FLIGHT_GENERATION_STATUSES,
  PostProduction,
  PostProductionGeneration,
} from '@/types/postProductionGeneration';
import QueryContext from '@/api/QueryContext';
import assetFetchers from '@/api/fetchers/asset';
import useDataQuery from '@/hooks/use-data-query';

export const POST_PRODUCTION_QUERY_KEY = 'postProduction';

const POLL_INTERVAL = 5000;

type PostProductionAsset = {postProduction: PostProduction | null};
type AssetQueryData = {data: PostProductionAsset} | undefined;

function isProcessingGenerations(
  generations: PostProductionGeneration[]
): boolean {
  return generations.some((v) =>
    IN_FLIGHT_GENERATION_STATUSES.includes(v.status)
  );
}

function usePostProductionGenerations({
  campaignId,
  assetId,
  enabled = true,
}: {
  campaignId: string | undefined;
  assetId: string | undefined;
  enabled?: boolean;
}) {
  const response = useDataQuery<PostProductionAsset>({
    queryKey: [POST_PRODUCTION_QUERY_KEY, assetId],
    queryFn: (context: QueryContext) => assetFetchers.campaignAsset(context),
    enabled: !!campaignId && !!assetId && enabled,
    refetchInterval: (data: AssetQueryData) =>
      isProcessingGenerations(data?.data?.postProduction?.generations ?? [])
        ? POLL_INTERVAL
        : false,
    meta: {campaignId, assetId},
  });

  const postProduction = response.data?.postProduction ?? null;

  return useMemo(() => {
    const generations = postProduction?.generations ?? [];
    return {
      generations,
      remainingResizes: postProduction?.remaining.resize,
      resizeLimit: postProduction?.limits.resize,
      isProcessing: isProcessingGenerations(generations),
      isLoading: response.isLoading,
    };
  }, [postProduction, response.isLoading]);
}

export default usePostProductionGenerations;
